import { useEffect } from "react";

import {
  SMTAnalyticsPageView,
  SMTEntryAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import { ModalTestID } from "@/constants/TestID";
import { AnimationDuration } from "@/constants/UI";
import useSMTWelcomeModal from "@/hooks/useSMTWelcomeModal";
import { useAnalytics } from "@/services/Analytics";
import BottomSheetModal from "@/ui/composed/modal/BottomSheetModal";
import { Button, FAIconName, Icon, Text } from "@/ui/core";
import { Box, HStack, VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

enum SMTWelcomeModalText {
  ButtonTitle = "Got it",
  DescriptionInstructions = "To start, just tap this button in the bottom corner of the home screen",
  DescriptionSteps = "Next, add a photo and any other comments you'd like to share. Make sure to double check the date and time, too!",
  DescriptionTop = "Sharing what you experience each day gives your provider team the valuable information they need to help you progress in treatment.",
  DismissAccessibilityLabel = "Dismiss Welcome Modal",
  Title = "Welcome to your Equip Journal",
}

enum SMTWelcomeModalTestID {
  DescriptionInstructions = "description-instructions",
  DescriptionSteps = "description-steps",
  DescriptionTop = "description-top",
  GetStartedButton = "get-started-button",
  JournalIcon = "journal-icon",
}

const IconSize = 54;

interface SMTWelcomeModalProps {
  isScreenFocused: boolean;
  onAnimationTrigger?: () => void;
}

const SMTWelcomeModal = ({
  isScreenFocused,
  onAnimationTrigger,
}: SMTWelcomeModalProps) => {
  const {
    handleWelcomeModalDismiss,
    isCheckingStoredValue,
    shouldShowWelcomeModal,
  } = useSMTWelcomeModal({ isScreenFocused });
  const baseTestID = joinTestIDs(ModalTestID.SelfMonitoring, "welcome-modal");
  const { track, trackPageView } = useAnalytics();

  const handleGotItCTA = () => {
    track(SMTEntryAnalyticsEvent.WelcomeModalConfirmPressed);
    handleWelcomeModalDismiss();
    // Trigger FAB animation after modal dismiss with small delay
    setTimeout(() => {
      onAnimationTrigger?.();
    }, AnimationDuration.Default);
  };

  const onRequestClose = () => {
    track(SMTEntryAnalyticsEvent.WelcomeModalDismissed);
    handleWelcomeModalDismiss();
    // Trigger FAB animation after modal dismiss with small delay
    setTimeout(() => {
      onAnimationTrigger?.();
    }, AnimationDuration.Default);
  };

  useEffect(() => {
    if (shouldShowWelcomeModal && !isCheckingStoredValue) {
      trackPageView(SMTAnalyticsPageView.WelcomeModalPageView);
    }
  }, [shouldShowWelcomeModal, isCheckingStoredValue, trackPageView]);

  return (
    <BottomSheetModal
      dismissAccessibilityLabel={SMTWelcomeModalText.DismissAccessibilityLabel}
      isOpen={shouldShowWelcomeModal}
      onRequestClose={onRequestClose}
      testID={baseTestID}
      title={SMTWelcomeModalText.Title}
    >
      <VStack alignItems="center" gap={24} paddingVertical={16}>
        <Text
          testID={joinTestIDs(baseTestID, SMTWelcomeModalTestID.DescriptionTop)}
        >
          {SMTWelcomeModalText.DescriptionTop}
        </Text>

        <Box
          alignItems="center"
          backgroundColor={ColorToken.AccentBlue}
          borderRadius={IconSize / 2}
          height={IconSize}
          justifyContent="center"
          width={IconSize}
        >
          <Icon
            color={ColorToken.NeutralBackgroundPrimary}
            name={FAIconName.Plus}
            size="lg"
            testID={joinTestIDs(baseTestID, SMTWelcomeModalTestID.JournalIcon)}
          />
        </Box>

        <Text
          testID={joinTestIDs(
            baseTestID,
            SMTWelcomeModalTestID.DescriptionInstructions,
          )}
          textAlign="center"
          variant="bodyMedium"
        >
          {SMTWelcomeModalText.DescriptionInstructions}
        </Text>
        <Text
          testID={joinTestIDs(
            baseTestID,
            SMTWelcomeModalTestID.DescriptionSteps,
          )}
          textAlign="center"
          variant="bodyMedium"
        >
          {SMTWelcomeModalText.DescriptionSteps}
        </Text>
        <HStack justifyContent="center" width="100%">
          <Button
            isFullWidth
            leftIcon={FAIconName.Check}
            onPress={handleGotItCTA}
            testID={joinTestIDs(
              baseTestID,
              SMTWelcomeModalTestID.GetStartedButton,
            )}
            title={SMTWelcomeModalText.ButtonTitle}
          />
        </HStack>
      </VStack>
    </BottomSheetModal>
  );
};

export { SMTWelcomeModalTestID, SMTWelcomeModalText };
export default SMTWelcomeModal;
